import { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { inviteUser, inviteUserData } from "../types";

const debouncedCache: Record<string, ReturnType<typeof debounce>> = {};

export const useInviteUserTableData = () => {
  const [inviteList, setInviteList] = useState<inviteUser>();
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedInviteUserData, setSelectedInviteUserData] = useState<inviteUser[]>([]);

  useEffect(() => {
    const inviteMap = inviteList?.data?.reduce<Record<string, inviteUser>>((acc, invite) => {
      acc[invite?._id] = invite;
      return acc;
    }, {});

    const selected = Object.keys(rowSelection)
      .filter(rowId => rowSelection[rowId])
      .map(rowId => inviteMap?.[rowId])
      .filter(Boolean);

    setSelectedInviteUserData(selected);
  }, [rowSelection, inviteList]);

  const removeUser = useCallback((_id: string) => {
    // setInviteList(prev => ({
    //   ...prev,
    //   data: prev?.data?.filter(item => item?._id !== _id),
    // }));

    setInviteList(prev => {
      const deletedUser = prev?.data?.find(item => item?._id === _id);
      const { productID } = deletedUser;
      const productSeats = prev.selectedInvoiceSeats?.[productID];
      const updatedSeats = { ...prev.selectedInvoiceSeats };

      if(productSeats){
        const invoiceKeys = Object.keys(productSeats);

        for (const invoiceKey of invoiceKeys) {
          if (productSeats[invoiceKey] > 1) {
            updatedSeats[productID] = {
              ...productSeats,
              [invoiceKey]: productSeats[invoiceKey] - 1
            };
          } else {
            updatedSeats[productID] = {
              ...productSeats,
              [invoiceKey]: 0
            };

            delete updatedSeats[productID];
          }
          break;
        }
      }

      return {
        ...prev,
        data: prev.data.filter(item => item._id !== _id),
        selectedInvoiceSeats: updatedSeats,
        startDate: prev.data?.length>1 ? prev?.startDate : ""
      };
    });
  }, []);

  const getDebouncedUpdater = useCallback((
    teamID: string,
    field: keyof inviteUserData
  ) => {
    const cacheKey = `${teamID}-${field}`;

    if (!debouncedCache[cacheKey]) {
      debouncedCache[cacheKey] = debounce((value: string) => {
        setInviteList((prev) => {
          if (!prev?.data) return prev;
          const updatedData = prev.data.map((item) =>
            item._id === teamID ? { ...item, [field]: value } : item
          );
          return { ...prev, data: updatedData };
        });
      }, 500);
    }

    return debouncedCache[cacheKey];
  }, []);

  const addNewInviteRow = useCallback(() => {
    const newEntry: inviteUserData = {
      _id: uuidv4(),
      first: "",
      last: "",
      email: "",
      productID:"",
      roles: [
        { id: uuidv4(), groupID: "", roleName: "", groupName: "" }
      ],
    };

    setInviteList((prev) => ({
      ...prev,
      data: [newEntry, ...(prev?.data || [])],
    }));
  }, []);

  return {
    inviteList,
    setInviteList,
    rowSelection,
    setRowSelection,
    selectedInviteUserData,
    removeUser,
    getDebouncedUpdater,
    addNewInviteRow,
  };
};
